/**
 * HUD: the top-right Pause + Fullscreen buttons drawn into a Pixi
 * Container. Owns its own hit-test rect array; the host's pointer
 * handler iterates `hits` and forwards taps.
 *
 * Stateless render: build() rebuilds from scratch and is called on
 * resize / fullscreen toggle.
 */

import { Container, Graphics } from 'pixi.js';

export interface HudHit {
  x: number; y: number; w: number; h: number;
  action: () => void;
}

export interface HudInsets { top: number; right: number; }

export interface HudBuildOptions {
  /** Renderer width in backing-buffer pixels (matches `app.renderer.width`). */
  width: number;
  insets: HudInsets;
  /** Whether the document is currently fullscreen (controls icon glyph). */
  isFullscreen: boolean;
  onTogglePause: () => void;
  onToggleFullscreen: () => void;
}

export class Hud {
  /** Pointer-tap rectangles for the current frame. */
  readonly hits: HudHit[] = [];

  constructor(private layer: Container) {}

  build(opts: HudBuildOptions): void {
    this.hits.length = 0;
    const { width: w, insets, isFullscreen } = opts;
    const size = 36;
    // 12px breathing room on top of the system safe-area inset so the
    // buttons sit clear of the iOS status bar / notch in PWA mode.
    const padTop = 12 + insets.top;
    const padRight = 12 + insets.right;

    // --- Pause button (top-right) ---
    const pauseX = w - size - padRight;
    const pauseY = padTop;
    const pauseBtn = new Graphics();
    pauseBtn.roundRect(pauseX, pauseY, size, size, 8)
      .fill({ color: 0x000000, alpha: 0.45 })
      .stroke({ color: 0xffffff, width: 2, alpha: 0.85 });
    const barW = 5, barH = 16, gap = 4;
    const barY = pauseY + (size - barH) / 2;
    const barX1 = pauseX + size / 2 - gap / 2 - barW;
    const barX2 = pauseX + size / 2 + gap / 2;
    pauseBtn.rect(barX1, barY, barW, barH).fill(0xffffff);
    pauseBtn.rect(barX2, barY, barW, barH).fill(0xffffff);
    this.layer.addChild(pauseBtn);
    this.hits.push({ x: pauseX, y: pauseY, w: size, h: size, action: opts.onTogglePause });

    // --- Fullscreen button (left of pause) ---
    const fsX = pauseX - size - 8;
    const fsY = padTop;
    const fsBtn = new Graphics();
    fsBtn.roundRect(fsX, fsY, size, size, 8)
      .fill({ color: 0x000000, alpha: 0.45 })
      .stroke({ color: 0xffffff, width: 2, alpha: 0.85 });
    // Icon: corner brackets. Different glyph if already fullscreen (arrows in).
    const m = 8;       // margin from button edge
    const armLen = 9;  // bracket arm length
    const lw = 2.5;
    const drawCorner = (cx: number, cy: number, dx: number, dy: number): void => {
      fsBtn.moveTo(cx + dx * armLen, cy).lineTo(cx, cy).lineTo(cx, cy + dy * armLen)
        .stroke({ color: 0xffffff, width: lw });
    };
    if (!isFullscreen) {
      // Outward-pointing brackets at each corner = "go fullscreen".
      drawCorner(fsX + m,         fsY + m,         1,  1);
      drawCorner(fsX + size - m,  fsY + m,        -1,  1);
      drawCorner(fsX + m,         fsY + size - m,  1, -1);
      drawCorner(fsX + size - m,  fsY + size - m, -1, -1);
    } else {
      // Inward-pointing brackets = "exit fullscreen".
      const m2 = 6;
      drawCorner(fsX + size / 2 - m2, fsY + size / 2 - m2, -1, -1);
      drawCorner(fsX + size / 2 + m2, fsY + size / 2 - m2,  1, -1);
      drawCorner(fsX + size / 2 - m2, fsY + size / 2 + m2, -1,  1);
      drawCorner(fsX + size / 2 + m2, fsY + size / 2 + m2,  1,  1);
    }
    this.layer.addChild(fsBtn);
    this.hits.push({ x: fsX, y: fsY, w: size, h: size, action: opts.onToggleFullscreen });
  }
}

/** Toggle between fullscreen and windowed, with a webkit fallback for
 *  older Safari. Idempotent — no-op if the requested state is already
 *  active. */
export function toggleFullscreen(): void {
  const doc = document as Document & {
    webkitExitFullscreen?: () => Promise<void>;
    webkitFullscreenElement?: Element | null;
  };
  const el = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void>;
  };
  const inFs = !!(document.fullscreenElement || doc.webkitFullscreenElement);
  if (inFs) {
    const exit = document.exitFullscreen?.bind(document) ?? doc.webkitExitFullscreen?.bind(doc);
    if (exit) void exit();
  } else {
    const req = el.requestFullscreen?.bind(el) ?? el.webkitRequestFullscreen?.bind(el);
    if (req) void req();
  }
}
